/* Phase 2E-lite — Distribution Opportunity Panel.
 *
 * Renders the deterministic, rule-based channel recommendations
 * produced by `backend/distribution_intelligence.py`. The panel is
 * honesty-first (Section 8 of the spec):
 *   - Top banner explicitly labels output as AI INFERENCE — starting
 *     points, NOT verified channel data.
 *   - Every row carries source_label + confidence pills.
 *   - "Manual verification recommended" footer line per row.
 *
 * Backend contract:
 *   - GET  /api/projects/{id}/distribution-opportunities
 *   - POST /api/projects/{id}/distribution-opportunities/generate
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Compass, RefreshCw, Sparkles, AlertCircle, Target, MapPin,
    Users, Megaphone, Store, Handshake, ShoppingBag, ListChecks,
} from "lucide-react";

const ROUTE_TONE = {
    organic:      "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
    paid:         "border-rose-500/30 text-rose-300 bg-rose-500/5",
    influencer:   "border-fuchsia-500/30 text-fuchsia-300 bg-fuchsia-500/5",
    listing:      "border-sky-500/30 text-sky-300 bg-sky-500/5",
    partnership:  "border-amber-500/30 text-amber-300 bg-amber-500/5",
    marketplace:  "border-indigo-500/30 text-indigo-300 bg-indigo-500/5",
};

const IMPACT_TONE = {
    high:   "border-emerald-500/40 text-emerald-200 bg-emerald-500/10",
    medium: "border-amber-500/40 text-amber-200 bg-amber-500/10",
    low:    "border-zinc-700 text-zinc-300 bg-zinc-900/40",
};

const DIFFICULTY_TONE = {
    low:    "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
    medium: "border-amber-500/30 text-amber-300 bg-amber-500/5",
    high:   "border-rose-500/30 text-rose-300 bg-rose-500/5",
};

const TYPE_ICON = {
    local:        MapPin,
    community:    Users,
    influencer:   Megaphone,
    press:        Megaphone,
    listing:      ListChecks,
    marketplace:  ShoppingBag,
    partnership:  Handshake,
    platform:     Store,
};

function prettyLabel(s) {
    return (s || "").replace(/_/g, " ");
}

export default function DistributionOpportunityPanel({ projectId }) {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [honesty, setHonesty] = useState("");

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const { data } = await api.get(
                `/projects/${projectId}/distribution-opportunities`
            );
            setReport(data.report || null);
            setHonesty(data.honesty_notice || "");
        } catch (e) {
            toast.error(e.userMessage || "Could not load distribution opportunities");
        } finally { setLoading(false); }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    const generate = async () => {
        if (!projectId) return;
        setGenerating(true);
        try {
            const { data } = await api.post(
                `/projects/${projectId}/distribution-opportunities/generate`
            );
            toast.success(
                `Generated ${data.generated} channel ${
                    data.generated === 1 ? "recommendation" : "recommendations"
                }.`
            );
            await load();
        } catch (e) {
            toast.error(e.userMessage || "Could not generate recommendations");
        } finally { setGenerating(false); }
    };

    const opportunities = report?.opportunities || [];
    const signals = report?.input_signals || {};

    return (
        <div className="card-base p-5" data-testid="distribution-opportunity-panel">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <Compass className="w-4 h-4 text-zinc-500" />
                    <h3 className="font-display text-lg">Distribution opportunities</h3>
                    <span className="text-xs text-zinc-500" data-testid="distribution-count">
                        {opportunities.length} channel{opportunities.length === 1 ? "" : "s"}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={generate}
                    disabled={generating}
                    data-testid="distribution-generate-btn"
                    className="btn-secondary text-xs disabled:opacity-50"
                    title="Generate deterministic channel recommendations from industry, geography, audience and goal."
                >
                    {generating
                        ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        : <Sparkles className="w-3.5 h-3.5" />}
                    {report ? "Regenerate" : "Generate"}
                </button>
            </div>

            <div
                className="text-xs text-zinc-500 mb-4 leading-relaxed border-l-2 border-violet-500/30 pl-3 bg-violet-500/[0.03] py-2 rounded-r-sm"
                data-testid="distribution-honesty-notice"
            >
                <span className="text-violet-300 font-medium">AI inference — starting points only.</span>{" "}
                {honesty || (
                    "Recommendations come from deterministic industry / geography / "
                    + "audience rule templates. They are NOT verified channel data and "
                    + "have NOT been validated against live platform APIs. Manual "
                    + "verification recommended before acting on any channel."
                )}
            </div>

            {loading && (
                <div className="text-xs text-zinc-500" data-testid="distribution-loading">
                    Loading…
                </div>
            )}

            {!loading && !report && (
                <div
                    className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-sm p-6 text-center"
                    data-testid="distribution-empty"
                >
                    No distribution opportunities generated yet.
                    <div className="text-xs mt-2 text-zinc-600">
                        Click <span className="text-zinc-300">Generate</span> above to
                        build a deterministic channel map from your client's industry,
                        geography and audience.
                    </div>
                </div>
            )}

            {!loading && report && (
                <>
                    <SignalsLine signals={signals} />
                    <div className="space-y-2.5" data-testid="distribution-rows">
                        {opportunities.map((o, idx) => (
                            <OpportunityRow key={`${o.channel_name}-${idx}`} o={o} idx={idx} />
                        ))}
                    </div>
                    <div
                        className="mt-4 text-[10px] text-zinc-600 flex items-center gap-1.5"
                        data-testid="distribution-generated-by"
                    >
                        <AlertCircle className="w-3 h-3" />
                        Generated by {report.generated_by || "furmais_distribution_intelligence_v1"}
                        {report.generated_at
                            ? ` · ${new Date(report.generated_at).toLocaleString()}`
                            : ""}
                        {" · "}<span className="text-zinc-500">Manual verification recommended.</span>
                    </div>
                </>
            )}
        </div>
    );
}

function SignalsLine({ signals }) {
    const items = [];
    if (signals.industry)     items.push(["Industry", signals.industry]);
    if (signals.geography)    items.push(["Geography", signals.geography]);
    if (signals.audience)     items.push(["Audience", signals.audience]);
    if (signals.project_type) items.push(["Project", signals.project_type.replace(/_/g, " ")]);
    if (items.length === 0) return null;
    return (
        <div
            className="mb-3 flex flex-wrap gap-2 text-[11px] text-zinc-500"
            data-testid="distribution-signals"
        >
            <Target className="w-3 h-3 mt-0.5 text-zinc-600" />
            {items.map(([k, v]) => (
                <span key={k} className="font-mono uppercase tracking-wider">
                    <span className="text-zinc-600">{k}:</span>{" "}
                    <span className="text-zinc-300">{String(v).slice(0, 60)}</span>
                </span>
            ))}
        </div>
    );
}

function OpportunityRow({ o, idx }) {
    const Icon = TYPE_ICON[o.channel_type] || Compass;
    const routeClass = ROUTE_TONE[o.recommended_route] || ROUTE_TONE.organic;
    const impactClass = IMPACT_TONE[o.visibility_impact] || IMPACT_TONE.low;
    const diffClass  = DIFFICULTY_TONE[o.difficulty] || DIFFICULTY_TONE.medium;
    return (
        <div
            className="border border-zinc-800 bg-zinc-950/50 rounded-sm px-3 py-3"
            data-testid={`distribution-row-${idx}`}
        >
            <div className="flex items-center justify-between gap-3 flex-wrap mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                    <span
                        className="text-sm text-zinc-100 font-medium truncate"
                        data-testid={`distribution-row-name-${idx}`}
                    >
                        {o.channel_name}
                    </span>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        {prettyLabel(o.channel_type)}
                    </span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                        className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${routeClass}`}
                        data-testid={`distribution-row-route-${idx}`}
                    >
                        {prettyLabel(o.recommended_route)}
                    </span>
                    <span
                        className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${impactClass}`}
                        title="Visibility impact"
                    >
                        impact: {o.visibility_impact}
                    </span>
                    <span
                        className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${diffClass}`}
                        title="Difficulty"
                    >
                        difficulty: {o.difficulty}
                    </span>
                    <span
                        className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-violet-500/30 text-violet-300 bg-violet-500/5"
                        title="Source label"
                        data-testid={`distribution-row-source-${idx}`}
                    >
                        {prettyLabel(o.source_label || "ai_inference")}
                    </span>
                </div>
            </div>

            {o.why_it_matters && (
                <p className="text-[13px] text-zinc-300 leading-relaxed mb-2">
                    {o.why_it_matters}
                </p>
            )}

            {Array.isArray(o.setup_requirements) && o.setup_requirements.length > 0 && (
                <div className="mb-2">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                        Setup
                    </div>
                    <ul className="text-xs text-zinc-400 space-y-0.5 ml-4 list-disc">
                        {o.setup_requirements.map((r, i) => (
                            <li key={i}>{r}</li>
                        ))}
                    </ul>
                </div>
            )}

            {o.first_30_day_action && (
                <div className="text-xs text-zinc-300 border-l-2 border-emerald-500/30 pl-2.5 py-1 bg-emerald-500/[0.03] rounded-r-sm">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">
                        First 30 days:
                    </span>{" "}
                    {o.first_30_day_action}
                </div>
            )}

            <div className="mt-2 text-[10px] text-zinc-600 flex items-center gap-1.5">
                <AlertCircle className="w-2.5 h-2.5" />
                AI inference — manual verification recommended before acting.
            </div>
        </div>
    );
}
